import React, { useCallback } from 'react';
import { Line } from 'react-chartjs-2';
import { ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import type { KpiData } from './types';
import type { KpiDrilldownSpec } from './kpiDashboardApi';
import { SectionLabel } from './KpiDashboardContent';
import { KpiPrioritySlaChart } from './charts';
import KpiChangesMonthlyLineChart from './charts/KpiChangesMonthlyLineChart';
import KpiChangeAssignmentGroupComboChart from './charts/KpiChangeAssignmentGroupComboChart';
import KpiChangeCloseCodeDonut from './charts/KpiChangeCloseCodeDonut';
import KpiChangeLabeledBarChart from './charts/KpiChangeLabeledBarChart';
import { formatKpiRatePercentFromNumberString, formatKpiTwoDecimalDisplay } from './kpiDashboardNumberFormat';

export interface KpiChangeRequestDashboardTabProps {
  chartsData: KpiData | undefined;
  chartsLoading: boolean;
  chartsFetching: boolean;
  chartsError: Error | null;
  chartsOnRetry?: () => void;
  onDrill: (spec: KpiDrilldownSpec) => void;
}

function ChangeKpiSparkline({ values }: { values: number[] }) {
  const data = values;
  return (
    <div className="mt-2 h-10 w-full">
      <Line
        data={{
          labels: data.map((_, i) => i),
          datasets: [
            {
              data,
              borderColor: '#111827',
              backgroundColor: 'transparent',
              borderWidth: 1.5,
              tension: 0.35,
              pointRadius: 0,
              spanGaps: true,
            },
          ],
        }}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false }, tooltip: { enabled: false } },
          scales: {
            x: { display: false },
            y: { display: false, min: 0 },
          },
        }}
      />
    </div>
  );
}

function ChangeKpiHeaderStrip({
  changeCount,
  successPct,
  failurePct,
  mttr,
  trend,
  onMetric,
}: {
  changeCount: string;
  successPct: string;
  failurePct: string;
  mttr: string;
  trend: number[];
  onMetric: (key: string) => void;
}) {
  const items = [
    { label: 'Change Count KPI', value: changeCount, drillKey: 'changes_volume' as const, spark: true },
    { label: 'Success Rate %', value: successPct, drillKey: 'change_success' as const, spark: false },
    { label: 'Failure Rate %', value: failurePct, drillKey: 'change_failure' as const, spark: false },
    { label: 'MTTR (Minutes)', value: mttr, drillKey: 'changes_mttr' as const, spark: false },
  ];

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-sm shadow-slate-900/5 ring-1 ring-slate-950/[0.04]">
      <div className="h-1 bg-gradient-to-r from-[#3465a4] via-[#5b8fce] to-[#00ba38]" aria-hidden />
      <div className="grid grid-cols-2 divide-x divide-y divide-slate-100 sm:grid-cols-2 lg:grid-cols-4 lg:divide-y-0">
        {items.map((item) => (
          <button
            key={item.label}
            type="button"
            onClick={() => onMetric(item.drillKey)}
            className="flex flex-col items-start px-4 py-4 text-left transition-colors hover:bg-slate-50/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#3465a4] sm:px-5"
          >
            <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{item.label}</span>
            <span className="mt-1 text-2xl font-bold tabular-nums tracking-tight text-slate-900 sm:text-3xl">
              {item.value}
            </span>
            {item.spark ? <ChangeKpiSparkline values={trend} /> : null}
          </button>
        ))}
      </div>
    </div>
  );
}

const KpiChangeRequestDashboardTab: React.FC<KpiChangeRequestDashboardTabProps> = ({
  chartsData,
  chartsLoading,
  chartsFetching,
  chartsError,
  chartsOnRetry,
  onDrill,
}) => {
  const openMetric = useCallback(
    (key: string) => {
      onDrill({ kind: 'metric', key });
    },
    [onDrill]
  );

  const openPriority = useCallback(
    (priority: string) => {
      onDrill({ kind: 'priority', priority });
    },
    [onDrill]
  );

  const openTrendMonth = useCallback(
    (monthIndex: number) => {
      onDrill({ kind: 'trend', monthIndex, series: 'Change' });
    },
    [onDrill]
  );

  const openAssignmentGroup = useCallback(
    (groupName: string) => {
      onDrill({ kind: 'assignment_group', groupName, ticketType: 'CHANGE' });
    },
    [onDrill]
  );

  const openChangeChart = useCallback(
    (chartKind: 'close_code' | 'state' | 'change_type' | 'failure_by_type', value: string) => {
      if (!value) return;
      onDrill({ kind: 'change_chart', chartKind, value });
    },
    [onDrill]
  );

  const loading = chartsLoading && !chartsData;
  if (loading) {
    return (
      <div className="space-y-8" aria-busy="true" aria-label="Loading change request dashboard">
        <div className="h-36 animate-pulse rounded-xl bg-slate-200/80" />
        <div className="h-36 animate-pulse rounded-xl bg-slate-200/80" />
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="h-72 min-h-0 animate-pulse rounded-xl bg-slate-200/80" />
          <div className="h-72 min-h-0 animate-pulse rounded-xl bg-slate-200/80" />
        </div>
      </div>
    );
  }

  const blockingError = chartsError && !chartsData;
  if (blockingError) {
    return (
      <div className="rounded-xl border border-red-200/80 bg-white p-8 text-center shadow-sm">
        <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-red-500" aria-hidden />
        <p className="mt-3 text-sm font-semibold text-red-900">Could not load change request dashboard</p>
        <p className="mt-1 text-sm text-red-700/90">{chartsError?.message}</p>
        {chartsOnRetry && (
          <button
            type="button"
            onClick={() => chartsOnRetry()}
            className="mt-4 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
          >
            Try again
          </button>
        )}
      </div>
    );
  }

  if (!chartsData) return null;

  const failRate = chartsData.changeFailureRate;
  const successRate = chartsData.changesSuccessRate;
  const mttr =
    chartsData.changesMttrMinutes !== '—' ? chartsData.changesMttrMinutes : chartsData.mttrMinutes;

  const donutSlices = chartsData.changesCloseCodeSlices.filter((s) => s.count > 0);

  return (
    <div className="relative">
      {chartsFetching && (
        <div
          className="pointer-events-none absolute right-0 top-0 z-10 flex items-center gap-1.5 rounded-full border border-slate-200/80 bg-white/95 px-3 py-1.5 text-xs font-medium text-slate-700 shadow-md backdrop-blur-sm"
          aria-live="polite"
        >
          <ArrowPathIcon className="h-3.5 w-3.5 animate-spin text-[#3465a4]" aria-hidden />
          Updating
        </div>
      )}
      {chartsError && chartsData && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-amber-200/90 bg-amber-50/90 px-4 py-3 text-sm text-amber-950 shadow-sm">
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-amber-600" aria-hidden />
          <span className="min-w-0 flex-1">Showing cached data. Latest refresh failed: {chartsError.message}</span>
          <button
            type="button"
            onClick={() => chartsOnRetry?.()}
            className="shrink-0 font-semibold text-amber-900 underline decoration-amber-600/50 underline-offset-2 hover:text-amber-950"
          >
            Retry
          </button>
        </div>
      )}

      <section className="mb-6">
        <SectionLabel>Change KPIs</SectionLabel>
        <div className="w-full">
          <ChangeKpiHeaderStrip
            changeCount={chartsData.changesVolume.toLocaleString()}
            successPct={formatKpiRatePercentFromNumberString(successRate)}
            failurePct={formatKpiRatePercentFromNumberString(failRate)}
            mttr={formatKpiTwoDecimalDisplay(mttr)}
            trend={chartsData.monthlyTrendChange}
            onMetric={openMetric}
          />
        </div>
      </section>

      <section className="mb-6">
        <SectionLabel>Changes by date &amp; assignment group</SectionLabel>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:items-stretch">
          <div className="flex min-h-[300px] min-w-0 flex-col">
            <KpiChangesMonthlyLineChart
              monthlyTrendChange={chartsData.monthlyTrendChange}
              monthLabels={chartsData.monthlyTrendMonthLabels}
              onDrillMonth={openTrendMonth}
            />
          </div>
          <div className="min-w-0">
            <KpiChangeAssignmentGroupComboChart
              rows={chartsData.changesByAssignmentGroup}
              onGroupClick={openAssignmentGroup}
            />
          </div>
        </div>
      </section>

      <section className="mb-6">
        <SectionLabel>Priority &amp; change type</SectionLabel>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:items-stretch">
          <div className="min-w-0">
            <KpiPrioritySlaChart
              title="Changes by Priority"
              priorityCounts={chartsData.priorityCounts}
              slaBreachPct={chartsData.slaBreachPct}
              onPriorityBarClick={openPriority}
              variant="countOnly"
            />
          </div>
          <div className="min-w-0">
            <KpiChangeLabeledBarChart
              title="Change by Type"
              labels={chartsData.changesByChangeType.map((r) => r.typeLabel)}
              values={chartsData.changesByChangeType.map((r) => r.count)}
              datasetLabel="Changes"
              valueFormat="count"
              onBarClick={(i) => {
                const row = chartsData.changesByChangeType[i];
                if (row?.typeKey) openChangeChart('change_type', row.typeKey);
              }}
            />
          </div>
        </div>
      </section>

      <section className="mb-6">
        <SectionLabel>Close outcomes &amp; state</SectionLabel>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:items-stretch">
          <div className="min-w-0">
            <KpiChangeCloseCodeDonut
              slices={donutSlices.length ? donutSlices : chartsData.changesCloseCodeSlices}
              onSliceClick={(key) => openChangeChart('close_code', key)}
            />
          </div>
          <div className="min-w-0">
            <KpiChangeLabeledBarChart
              title="Changes by State"
              labels={chartsData.changesByState.map((r) => r.stateLabel)}
              values={chartsData.changesByState.map((r) => r.count)}
              datasetLabel="Changes"
              valueFormat="count"
              onBarClick={(i) => {
                const row = chartsData.changesByState[i];
                if (row?.stateKey) openChangeChart('state', row.stateKey);
              }}
            />
          </div>
        </div>
      </section>

      <section className="mb-6">
        <SectionLabel>Failure rate by type</SectionLabel>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:items-stretch">
          <div className="min-w-0">
            <KpiChangeLabeledBarChart
              title="% Change Failure by Type"
              labels={chartsData.changeFailureRateByType.map((r) => r.typeLabel)}
              values={chartsData.changeFailureRateByType.map((r) => r.failurePct)}
              datasetLabel="Failure %"
              valueFormat="percent"
              onBarClick={(i) => {
                const row = chartsData.changeFailureRateByType[i];
                if (row?.typeKey) openChangeChart('failure_by_type', row.typeKey);
              }}
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default KpiChangeRequestDashboardTab;
